import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, ChevronLeft, X } from "lucide-react";

// Sdílený obal panelu notifikací pro VŠECHNY role (21.7.2026).
//
// Mobil (< 640 px): panel PŘES CELOU OBRAZOVKU — stejně jako klientský portál.
//   Renderuje se portálem do <body>, aby ho nedusil stacking context TopBaru
//   (sticky z-20) ani sidebar (z-40). Větší písmo, 44px zavírací tlačítko,
//   safe-area nahoře i dole, zamčený scroll pozadí.
// Desktop (sm+): původní dropdown zakotvený pod zvonečkem — beze změny chování.
//
// Panel nese data-notif-panel, aby ho "klik mimo" handlery poznaly i v portálu
// (portálovaný DOM není potomkem refu zvonečku).

const MQ = "(max-width: 639.98px)";

export function useIsMobileNotif() {
  const [mob, setMob] = useState(
    () => (typeof window !== "undefined" ? window.matchMedia(MQ).matches : false)
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(MQ);
    const h = (e) => setMob(e.matches);
    setMob(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", h);
    else mq.addListener(h);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", h);
      else mq.removeListener(h);
    };
  }, []);
  return mob;
}

export default function NotifPanel({
  open,
  onClose,
  title = "Notifikace",
  count = 0,
  iconClass = "text-rose-500",
  actions = null,
  widthClass = "sm:w-[min(26rem,92vw)]",
  children,
}) {
  const mobile = useIsMobileNotif();

  // Escape zavírá panel (mobil i desktop).
  useEffect(() => {
    if (!open) return;
    const esc = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [open, onClose]);

  // Na mobilu je panel přes celou obrazovku — zamkni scroll stránky pod ním.
  useEffect(() => {
    if (!open || !mobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open, mobile]);

  if (!open) return null;

  const badge = count > 0 && (
    <span className="text-xs font-bold text-white bg-rose-500 rounded-full px-2 py-0.5 tabular-nums">
      {count}
    </span>
  );

  if (mobile) {
    return createPortal(
      <div
        data-notif-panel
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="fixed inset-0 z-[80] bg-white flex flex-col text-slate-800"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-200 shrink-0">
          {/* 4.9.2026: zavírání je tlačítkem zpět vlevo, stejně jako v okně
              Nahlásit / Zpětná vazba. Křížek vpravo je pryč. */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Zpět"
            title="Zpět"
            className="w-11 h-11 shrink-0 flex items-center justify-center rounded-xl border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 active:bg-slate-100"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <Bell className={`w-5 h-5 shrink-0 ${iconClass}`} />
          <span className="min-w-0 flex-1 truncate text-[17px] font-bold leading-tight">{title}</span>
          {badge}
          {actions}
        </div>
        <div
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          {children}
        </div>
      </div>,
      document.body
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden="true" />
      <div
        data-notif-panel
        role="dialog"
        aria-label={title}
        className={`absolute right-0 top-full mt-2 z-50 ${widthClass} bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden text-slate-800`}
      >
        <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-slate-100">
          <Bell className={`w-4 h-4 ${iconClass}`} />
          <span className="font-bold text-sm">{title}</span>
          {badge}
          <div className="ml-auto flex items-center gap-1">
            {actions}
            <button
              type="button"
              onClick={onClose}
              aria-label="Zavřít"
              className="p-1.5 rounded-xl text-slate-400 hover:bg-slate-100 min-h-[44px] sm:min-h-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        {children}
      </div>
    </>
  );
}
